/**
 * Canvas CSV export (CANVAS03, Phalcon study rank 4).
 *
 * Tabular export of what is drawn on the Case Canvas (nodes and edges).
 * Header row carries the same provenance stamp as the PNG:
 *   - demo (/fusion):           SYNTHETIC DATASET · Fusion Canvas · <day>
 *   - real (/cases/:id/canvas): Case <id> · court-safe state · <day>
 *
 * Invariant 6: Disabled while the demo overlay is on.
 * Court-safe mode leaves out scored elements, as the PNG does.
 * Note texts are NOT included in the CSV (officer text caveat; stated in header).
 */
import type cytoscape from 'cytoscape';
import type { SnapshotOpts } from './snapshot';

export const CSV_HEADER_COLUMNS = [
  'kind',
  'id',
  'type',
  'module',
  'system_label',
  'officer_label',
  'address',
  'chain',
  'state',
  'tier',
  'note_count',
  'source',
  'target',
  'edge_type',
  'determinism',
  'value_per_token',
  'transfer_count',
  'stale_or_gap_reason',
  'provenance_source',
] as const;

export function csvEscape(v: unknown): string {
  if (v === undefined || v === null) return '';
  let s = String(v);

  // Spreadsheet formula guard: prefix non-numeric cells starting with =, +, -, or @
  // (after optional leading whitespace/control characters) with a single quote (')
  // so Excel and LibreOffice never execute them as formulas. Genuine numbers are preserved.
  const match = s.match(/^[\s\x00-\x1f]*([=+\-@])/);
  if (match) {
    const firstChar = match[1];
    const isGenuineNumber =
      (typeof v === 'number' && Number.isFinite(v)) ||
      ((firstChar === '+' || firstChar === '-') &&
        /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(s.trim()));

    if (!isGenuineNumber) {
      s = `'${s}`;
    }
  }

  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export const escapeCsvCell = csvEscape;
export const csvCell = csvEscape;


/** Format per-token amounts cleanly for CSV. */
function formatAmountsByToken(amounts?: Record<string, number> | null): string {
  if (!amounts || typeof amounts !== 'object') return '';
  return Object.entries(amounts)
    .filter(([, val]) => typeof val === 'number' && Number.isFinite(val))
    .map(([tok, val]) => `${val} ${tok}`)
    .join('; ');
}

/** Build the tabular CSV representation of drawn canvas elements. */
export function buildCanvasCsv(cy: cytoscape.Core, opts: SnapshotOpts): string {
  const stamp = opts.mode === 'demo'
    ? `SYNTHETIC DATASET · Fusion Canvas · ${opts.day}`
    : `Case ${opts.caseId ?? '—'} · ${opts.courtSafe ? 'court-safe' : 'full'} · ${opts.day}`;

  const lines: string[] = [];

  // Header comment / stamp row
  lines.push(`# STAMP: ${stamp} | NOTE: Officer note texts are withheld from CSV export`);
  lines.push(CSV_HEADER_COLUMNS.join(','));

  const eles = cy.elements();

  // Court-safe filtering: exclude scored elements (as PNG does)
  const isCourtSafe = opts.courtSafe;

  // 1. Process Nodes
  eles.nodes().forEach((n) => {
    // Check visibility
    if (n.hasClass('cs-stephidden') || n.hasClass('cs-modhidden')) return;
    if (isCourtSafe && n.hasClass('cs-dim')) return;

    const data = n.data();
    const id = data.id || '';
    const type = data.type || '';
    const module = data.module || '';
    const systemLabel = data.system_label || data.label || id;
    const officerLabel = data.officer_label || '';
    const address = data.address || data.canonical_id || '';
    const chain = data.chain || '';
    const state = data.dormant === true ? 'dormant' : (data.state || 'active');
    const tier = data.attribution?.tier !== undefined ? String(data.attribution.tier) : '';
    const noteCount = Number(data.note_count) || 0;

    const row = [
      'node',
      csvEscape(id),
      csvEscape(type),
      csvEscape(module),
      csvEscape(systemLabel),
      csvEscape(officerLabel),
      csvEscape(address),
      csvEscape(chain),
      csvEscape(state),
      csvEscape(tier),
      csvEscape(noteCount),
      '', // source
      '', // target
      '', // edge_type
      '', // determinism
      '', // value_per_token
      '', // transfer_count
      '', // stale_or_gap_reason
      '', // provenance_source
    ];
    lines.push(row.join(','));
  });

  // 2. Process Edges
  eles.edges().forEach((e) => {
    if (e.hasClass('cs-stephidden') || e.hasClass('cs-modhidden')) return;
    const data = e.data();
    const determinism = data.determinism || 'deterministic';

    // Court-safe mode leaves out scored elements
    if (isCourtSafe && (determinism === 'scored' || e.hasClass('cs-dim'))) {
      return;
    }

    const source = data.source || '';
    const target = data.target || '';
    const edgeType = data.edge_type || '';

    let valuePerToken = '';
    if (data.amounts_by_token && Object.keys(data.amounts_by_token).length > 0) {
      valuePerToken = formatAmountsByToken(data.amounts_by_token);
    } else if (typeof data.value === 'number') {
      const tok = data.token_symbol || data.token || '';
      valuePerToken = tok ? `${data.value} ${tok}` : String(data.value);
    }

    const transferCount = data.collapsed_edge_count
      || (Array.isArray(data.transfers) ? data.transfers.length : (data.value ? 1 : 0));

    let staleOrGapReason = '';
    if (data.coverage_gap === true) {
      staleOrGapReason = 'coverage_gap';
    } else if (data.stale && typeof data.stale === 'object') {
      staleOrGapReason = String(data.stale.reason || 'stale');
    }

    const provenanceSource = data.attribution?.source || data.source_layer || '';

    const row = [
      'edge',
      '', // id
      '', // type
      '', // module
      '', // system_label
      '', // officer_label
      '', // address
      '', // chain
      '', // state
      '', // tier
      '', // note_count
      csvEscape(source),
      csvEscape(target),
      csvEscape(edgeType),
      csvEscape(determinism),
      csvEscape(valuePerToken),
      csvEscape(transferCount),
      csvEscape(staleOrGapReason),
      csvEscape(provenanceSource),
    ];
    lines.push(row.join(','));
  });

  return lines.join('\n');
}

/** Download the CSV export of drawn canvas elements. */
export function exportCanvasCsv(cy: cytoscape.Core, opts: SnapshotOpts): void {
  try {
    const csvContent = buildCanvasCsv(cy, opts);
    const tier = opts.courtSafe ? 'courtsafe' : 'full';
    const base = opts.mode === 'demo' ? 'case-canvas-demo' : `case-canvas-${opts.caseId ?? 'case'}`;
    const filename = `${base}-${tier}-${opts.day}.csv`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch {
    // gracefully ignore download errors
  }
}
